/**
 * Timeline Editor — bottom-sliding composition panel (video+image+audio).
 *
 * Key interactions:
 *   • Clip blocks are draggable horizontally on the pointer — they clamp to t>=0.
 *   • Ruler and every track lane share the SAME horizontal origin (left column
 *     is `position: absolute`, lanes start at x=0), so t=0 is pixel-aligned.
 *   • Play/Pause transport runs the playhead via requestAnimationFrame;
 *     a background <canvas> compositor renders the video/image layer in sync,
 *     while one <audio> per active audio clip plays with correct offsets.
 *   • Individual per-clip preview still available on click (shown above the
 *     master preview when paused).
 */
import {
  useEffect, useMemo, useRef, useState, useCallback,
} from 'react';
import {
  X, Upload, Image as ImageIcon, Video, Music, Play, Pause,
  Scissors, Trash2, Plus, Download, Loader2, CheckCircle, AlertCircle, FolderOpen,
  Waves, Sliders, Film, Volume2, VolumeX, ZoomIn, ZoomOut,
  LayoutTemplate, BookmarkPlus, AlertTriangle, GripVertical, Crosshair,
  Type, Sticker as StickerIcon, Star, Users, ArrowRightLeft, MoveHorizontal,
  PenLine,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import useTimelineStore, { trackIdFor, usePxPerSec, BASE_PX_PER_SEC, TIMELINE_TAIL_SECONDS, DEFAULT_TRACK_ORDER, trackAcceptsKind, docSignatureOf } from '../../store/timelineStore';
import { previewVolume } from '../../utils/audioModel';
import { galleryService } from '../../services/galleryService';
import { sendAssetToTimeline } from '../../utils/sendAssetToTimeline';
import { goToSourceNode } from '../../utils/goToSourceNode';
import {
  TIMELINE_SERVER_SYNC, SAVE_DEBOUNCE_MS, fetchTimeline, migrateTimeline,
} from '../../services/timelineSync';
import { uploadToR2 } from '../../utils/uploadToR2';
import { describeUploadError } from '../../utils/uploadLimits';
import {
  ElementInPreview, ElementTrack, ElementInspector, StickerPicker, TextPresetMenu,
} from './TimelineElements';
import { TIMELINE_TEMPLATES, templateService } from '../../services/templateService';
import SaveAsTemplateModal from './SaveAsTemplateModal';
import TimelineConflictModal from './TimelineConflictModal';
import TemplatesPanel from './TemplatesPanel';
import CompleteTemplatePanel from './CompleteTemplatePanel';
import TimelinePortal from './TimelinePortal';
// Envío UI-2 · Vista Edición (layout nuevo, detrás de flag)
import CanvasResultsBin from './CanvasResultsBin';
import { LAYOUT_V2 } from '../../layout/layoutV2';
// Envío UI-3 · panel Marca en el panel contextual
import BrandPanel from './BrandPanel';
import CharacterPanel from './CharacterPanel';
import { useWorkflowStore } from '../../store/workflowStore';
// Envío AP-3 · barra de estado, menú sobre el clip, imán, reemplazar y galería
import TimelineBar, { removeSelection, cutSelection } from './TimelineBar';
import ReplaceClipModal from './ReplaceClipModal';
import GalleryPanel from '../studio/GalleryPanel';
import { describeSelection } from './timelineBarModel';
import {
  SNAP_PX, findGaps, isSequential, snapEdges, snapStart,
} from '../../utils/timelineEdit';
import { useUploadLimits } from '../../hooks/useUploadLimits';
import { limitMB } from '../../utils/uploadLimits';
import { brandedFileName } from '../../utils/mediaUtils';
import { buildRenderPayload } from './renderPayload';
import {
  ImageEditCard, TimelineImageEditBox, TimelineImageEditPanel, findClip as findEditClip, requestImageEdit,
} from '../imageEdit/TimelineImageEdit';
import '../../styles/timeline.css';
import '../../styles/timeline-bar.css';

/** AP-3 · pide abrir «Reemplazar» para un clip (lo escucha el editor). */
export const requestReplace = (target) => window.dispatchEvent(
  new CustomEvent('sml:timeline-replace', { detail: target }),
);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * E3 · encaje `trim` en el PREVIEW: si el material dura menos que el hueco
 * (`freeze_tail`), el cabezal nunca pasa del final del clip — se queda en el
 * último frame, igual que hace `tpad` en el MP4 exportado.
 */
const clampPreviewTime = (clip, playheadTime) => {
  const raw = (clip.trim_start || 0) + (playheadTime - clip.start_time);
  const freeze = Number(clip.freeze_tail) || 0;
  if (freeze <= 0) return raw;
  const material = Number(clip.original_duration) > 0
    ? Number(clip.original_duration) - (clip.trim_start || 0)
    : Number(clip.duration || 0) - freeze;
  const end = Math.max(material, 0);
  if (end <= 0) return raw;
  return (clip.trim_start || 0) + Math.min(raw - (clip.trim_start || 0), end - 0.04);
};

const TARIFF = { '720p': 1.0, '1080p': 1.0, '4k': 3.0 };
export const RES_LABEL = { '720p': '720p HD', '1080p': '1080p FHD', '4k': '4K UHD' };
// El execution_id se guarda sólo como ayuda de navegación: la autoridad del
// estado del render es el backend (`GET /timeline/renders/active`).
const RENDER_EXEC_STORAGE_KEY = 'sml.timeline.activeRender';
const RENDER_STAGE_LABELS = {
  queued: 'En cola…',
  downloading: 'Descargando material…',
  preparing: 'Preparando clips…',
  rendering: 'Renderizando video…',
  uploading: 'Subiendo el MP4…',
};


/**
 * Live elapsed-time + ETA badge shown while a timeline render is in progress.
 * Empirically: ffmpeg renders ~real-time on 720p, ~0.5x on 1080p, ~0.15x on 4K.
 * We surface the elapsed seconds + the rough remaining estimate so the user
 * can see progress instead of staring at a generic spinner.
 */
const RENDER_SPEED = { '720p': 1.0, '1080p': 0.5, '4k': 0.18 };

const RenderEta = ({ startedAt, resolution, duration }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!startedAt) return null;
  const elapsed = Math.max(0, Math.floor((now - startedAt) / 1000));
  const speed = RENDER_SPEED[resolution] || 0.5;
  const total = Math.ceil(duration / speed) + 5; // +5s for upload/setup
  const remaining = Math.max(0, total - elapsed);
  return (
    <span className="tl-render-eta" data-testid="tl-render-eta">
      {elapsed}s · resta ~{remaining}s
    </span>
  );
};

// Pixels per second on the ruler / tracks (escala base; el zoom la multiplica)
const PX_PER_SEC = BASE_PX_PER_SEC;
// Ancho de la columna de etiquetas de pista (debe coincidir con --tl-gutter en timeline.css)
// UI-3: en el layout nuevo la columna de nombres de pista es más ancha (se leen).
const LABEL_GUTTER = LAYOUT_V2 ? 148 : 96;
// UI-3 · viñeta de la ventana X según lo seleccionado
const X_KINDS = {
  text: { label: 'Texto', color: '#fbbf24' },
  sticker: { label: 'Sticker', color: '#f472b6' },
  video: { label: 'Video', color: '#a78bfa' },
  image: { label: 'Imagen', color: '#60a5fa' },
  v1: { label: 'Superposición', color: '#c4b5fd' },
  v2: { label: 'Superposición', color: '#c4b5fd' },
  transition: { label: 'Transición', color: '#94a3b8' },
  audio: { label: 'Audio', color: '#34d399' },
};

// I-45 · la PRIMERA vez que alguien selecciona algo, un aviso chico en el
// panel le cuenta que se edita desde acá. Una sola vez por navegador.
const X_HINT_KEY = 'sml.v2.xHintSeen';
const XFirstHint = ({ label }) => {
  const [show, setShow] = useState(() => {
    try { return localStorage.getItem(X_HINT_KEY) !== '1'; } catch { return false; }
  });
  useEffect(() => {
    if (!show) return undefined;
    try { localStorage.setItem(X_HINT_KEY, '1'); } catch { /* privado */ }
    const t = setTimeout(() => setShow(false), 6000);
    return () => clearTimeout(t);
  }, [show]);
  if (!show) return null;
  const what = String(label || 'elemento').toLowerCase();
  return (
    <p className="tl-x-hint" role="status" data-testid="tl-x-hint">
      Editá {what === 'texto' ? 'este texto' : `este ${what}`} desde acá
    </p>
  );
};

// ────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────
const formatTime = (s) => {
  const m = Math.floor(s / 60);
  const r = s - m * 60;
  return `${m}:${r.toFixed(1).padStart(4, '0')}`;
};

export const calcCost = (duration, resolution) => {
  const raw = (duration / 10) * TARIFF[resolution];
  return Math.max(1, Math.ceil(raw));
};

const probeMediaDuration = (file) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const el = document.createElement(file.type.startsWith('video') ? 'video' : 'audio');
    el.preload = 'metadata';
    el.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(Number.isFinite(el.duration) ? el.duration : null); };
    el.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    el.src = url;
  });

// ────────────────────────────────────────────────────────────────
// Draggable Clip Block
// ────────────────────────────────────────────────────────────────
const ClipBlock = ({ clip, trackKind, isSelected, onClick }) => {
  const { updateClip, videoTransitions } = useTimelineStore();
  const pxPerSec = usePxPerSec();
  // E6 · Timeline en vivo: pulso ~1,5 s cuando LMX acaba de tocar este clip.
  const flashing = useTimelineStore((s) => s.agentFlash.includes(clip.id));
  const dragRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  // Effective fades = max(manual fade on clip, contribution from any
  // transition that touches this clip on the base video track). Rendered
  // live so the user sees the fade the second they insert a transition,
  // even before export.
  const isBaseVideo = trackKind === 'video';
  const transitionOut = isBaseVideo
    ? Math.max(0, ...videoTransitions.filter((t) => t.before_id === clip.id).map((t) => t.out_duration))
    : 0;
  const transitionIn = isBaseVideo
    ? Math.max(0, ...videoTransitions.filter((t) => t.after_id === clip.id).map((t) => t.in_duration))
    : 0;
  const effectiveFadeIn = Math.max(clip.fade_in || 0, transitionIn);
  const effectiveFadeOut = Math.max(clip.fade_out || 0, transitionOut);

  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const startX = e.clientX;
    const startTime = clip.start_time;
    let moved = false;

    // E2 · arrastre VERTICAL: la pista bajo el puntero se marca en verde si
    // acepta el tipo del clip y en rojo si no. Al soltar se mueve el clip
    // conservando recortes, volumen y transform (sólo cambia de array).
    const laneUnder = (ev) => {
      const el = document.elementFromPoint(ev.clientX, ev.clientY);
      return el?.closest?.('.tl-track')?.dataset?.track || null;
    };
    const clearHints = () => {
      document.querySelectorAll('.tl-track-drop-ok, .tl-track-drop-no')
        .forEach((el) => el.classList.remove('tl-track-drop-ok', 'tl-track-drop-no'));
    };
    let targetTrack = null;

    const startY = e.clientY;

    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      const dt = dx / pxPerSec;
      let newStart = Math.max(0, startTime + dt);
      if (!moved && (Math.abs(dx) > 2 || Math.abs(dy) > 4)) {
        moved = true;
        setIsDragging(true);
      }
      if (!moved) return;
      // AP-3 · imán: se engancha a los bordes de otros clips, al cabezal y al
      // inicio (con Alt se mueve libre). La guía se dibuja en las pistas.
      const tl = useTimelineStore.getState();
      if (tl.magnet && !ev.altKey) {
        const all = [...tl.videoClips, ...tl.imageClips, ...tl.overlayV1Clips,
          ...tl.overlayV2Clips, ...tl.audioClips];
        const snapped = snapStart(newStart, clip.duration,
          snapEdges(all, clip.id, tl.playheadTime), SNAP_PX / pxPerSec);
        newStart = snapped.start;
        if (tl.snapGuide !== snapped.guide) tl.setSnapGuide(snapped.guide);
      } else if (tl.snapGuide != null) {
        tl.setSnapGuide(null);
      }
      updateClip(trackKind, clip.id, { start_time: newStart });

      clearHints();
      const lane = laneUnder(ev);
      targetTrack = lane && lane !== trackKind ? lane : null;
      if (targetTrack) {
        const ok = trackAcceptsKind(targetTrack, clip.kind || (trackKind === 'audio' ? 'audio' : 'video'));
        const node = document.querySelector(`.tl-track[data-track="${targetTrack}"]`);
        node?.classList.add(ok ? 'tl-track-drop-ok' : 'tl-track-drop-no');
        if (!ok) targetTrack = null;
      }
    };
    const onUp = (ev) => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      clearHints();
      setIsDragging(false);
      useTimelineStore.getState().setSnapGuide(null);
      if (!moved) { onClick(); return; }
      if (targetTrack) {
        const store = useTimelineStore.getState();
        const dt = (ev.clientX - startX) / pxPerSec;
        const okMove = store.moveClipToTrack(trackKind, clip.id, targetTrack,
          Math.max(0, startTime + dt));
        if (okMove) {
          store.selectClip({ trackKind: targetTrack, id: clip.id });
          toast.success('Clip movido de pista', { position: 'bottom-right' });
        }
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const width = Math.max(clip.duration * pxPerSec, 32);
  const left = clip.start_time * pxPerSec;
  const Icon = trackKind === 'audio' ? Music : clip.kind === 'image' ? ImageIcon : Video;

  return (
    <div
      ref={dragRef}
      className={`tl-clip tl-clip-${trackKind} ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''} ${flashing ? 'lmx-flash' : ''}`}
      style={{ width, left }}
      onMouseDown={onMouseDown}
      data-testid={`tl-clip-${clip.id}`}
      title={`${clip.label || ''} · ${formatTime(clip.start_time)} → ${formatTime(clip.start_time + clip.duration)}`}
    >
      {trackKind !== 'audio' && clip.thumb_url && clip.kind === 'image' && (
        <img src={clip.thumb_url} alt="" className="tl-clip-thumb" draggable={false} />
      )}
      <div className="tl-clip-label">
        <Icon size={12} />
        <span>{clip.label || (trackKind === 'audio' ? 'Audio' : 'Clip')}</span>
      </div>
      {clip.edit?.original && (
        <span className="tl-clip-edited" title="Imagen editada (el original sigue en la Galería)" data-testid={`tl-clip-edited-${clip.id}`}>
          <PenLine size={9} aria-hidden="true" />Editada
        </span>
      )}
      <div className="tl-clip-duration">{clip.duration.toFixed(1)}s</div>

      {/* Visual preview of the fade-in / fade-out ramps. Rendered as a
          diagonal black gradient over the clip head/tail proportional to
          the fade duration relative to the clip's own duration. Combines
          manual fades AND any transition that touches this clip. */}
      {effectiveFadeIn > 0 && (
        <div
          className="tl-clip-fade-in"
          style={{ width: `${Math.min(100, (effectiveFadeIn / Math.max(clip.duration, 0.01)) * 100)}%` }}
          aria-hidden
        />
      )}
      {effectiveFadeOut > 0 && (
        <div
          className="tl-clip-fade-out"
          style={{ width: `${Math.min(100, (effectiveFadeOut / Math.max(clip.duration, 0.01)) * 100)}%` }}
          aria-hidden
        />
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────
// Track row
// ────────────────────────────────────────────────────────────────
// ────────────────────────────────────────────────────────────────
// Transition marker — small chip that sits at the boundary of two
// adjacent video clips. When a transition already exists it shows a
// filled pill; on the empty gap it renders a subtle "+" that appears
// on hover (see CSS `.tl-transition-slot`).
// ────────────────────────────────────────────────────────────────
const TransitionMarker = ({ leftClip, rightClip, transition, isSelected, onSelect, onCreate }) => {
  const pxPerSec = usePxPerSec();
  // Anchor position: right edge of the left clip (= left edge of the right clip).
  const anchorX = (leftClip.start_time + leftClip.duration) * pxPerSec;
  if (transition) {
    return (
      <button
        type="button"
        className={`tl-transition tl-transition-${transition.type} ${isSelected ? 'selected' : ''}`}
        style={{ left: anchorX }}
        onClick={(e) => { e.stopPropagation(); onSelect(transition.id); }}
        title={`Fundido a negro · ${transition.duration.toFixed(1)}s`}
        data-testid={`tl-transition-${transition.id}`}
      >
        <span className="tl-transition-icon">
          <Waves size={11} />
        </span>
        <span className="tl-transition-dur">{transition.duration.toFixed(1)}s</span>
      </button>
    );
  }
  return (
    <button
      type="button"
      className="tl-transition-slot"
      style={{ left: anchorX }}
      onClick={(e) => { e.stopPropagation(); onCreate(leftClip.id, rightClip.id); }}
      title="Insertar transición"
      aria-label="Insertar transición entre estos videos"
      data-testid={`tl-transition-add-${leftClip.id}-${rightClip.id}`}
    >
      <Plus size={10} strokeWidth={2.4} />
    </button>
  );
};

// ────────────────────────────────────────────────────────────────
// Salida general: mute y volumen master
// ────────────────────────────────────────────────────────────────
const MasterAudioControls = () => {
  const masterMuted = useTimelineStore((s) => s.masterMuted);
  const masterVolume = useTimelineStore((s) => s.masterVolume);
  const setMasterMuted = useTimelineStore((s) => s.setMasterMuted);
  const setMasterVolume = useTimelineStore((s) => s.setMasterVolume);

  return (
    <div className="tl-master-audio" data-testid="tl-master-audio">
      <button
        type="button"
        className={`tl-master-audio-btn ${masterMuted ? 'active' : ''}`}
        onClick={() => setMasterMuted(!masterMuted)}
        title={masterMuted ? 'Activar audio general' : 'Silenciar todo'}
        data-testid="tl-master-mute"
      >
        {masterMuted ? <VolumeX size={13} /> : <Volume2 size={13} />}
      </button>
      <input
        type="range"
        min={0} max={2} step={0.05}
        value={masterVolume}
        onChange={(e) => setMasterVolume(parseFloat(e.target.value))}
        className="tl-master-audio-range"
        title={`Volumen general: ${Math.round(masterVolume * 100)}%`}
        data-testid="tl-master-volume"
      />
    </div>
  );
};

// ────────────────────────────────────────────────────────────────
// Controles de audio por pista (mute / solo / volumen)
// ────────────────────────────────────────────────────────────────
const TrackAudioControls = ({ trackKind }) => {
  const trackId = trackIdFor(trackKind);
  const track = useTimelineStore((s) => s.tracks[trackId]);
  const toggleTrackMute = useTimelineStore((s) => s.toggleTrackMute);
  const toggleTrackSolo = useTimelineStore((s) => s.toggleTrackSolo);
  const setTrackAudio = useTimelineStore((s) => s.setTrackAudio);
  if (!track || trackKind === 'image') return null;

  return (
    <div className="tl-track-audio" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className={`tl-track-audio-btn ${track.muted ? 'active' : ''}`}
        onClick={() => toggleTrackMute(trackId)}
        title={track.muted ? 'Activar audio de la pista' : 'Silenciar pista'}
        data-testid={`tl-track-mute-${trackKind}`}
      >
        {track.muted ? <VolumeX size={11} /> : <Volume2 size={11} />}
      </button>
      <button
        type="button"
        className={`tl-track-audio-btn ${track.solo ? 'active' : ''}`}
        onClick={() => toggleTrackSolo(trackId)}
        title="Solo"
        data-testid={`tl-track-solo-${trackKind}`}
      >
        S
      </button>
      <input
        type="range"
        className="tl-track-audio-range"
        min={0} max={2} step={0.05}
        value={track.volume}
        onChange={(e) => setTrackAudio(trackId, { volume: parseFloat(e.target.value) })}
        title={`Volumen de pista: ${Math.round(track.volume * 100)}%`}
        data-testid={`tl-track-volume-${trackKind}`}
      />
    </div>
  );
};

// ────────────────────────────────────────────────────────────────
// AP-3 · menú sobre el clip elegido (Reemplazar · Cortar · Borrar)
// ────────────────────────────────────────────────────────────────
const ClipPill = ({ kind, clips }) => {
  const selectedClip = useTimelineStore((s) => s.selectedClip);
  const enabled = useTimelineStore((s) => s.clipPill);
  const playheadTime = useTimelineStore((s) => s.playheadTime);
  const pxPerSec = usePxPerSec();
  if (!enabled || selectedClip?.trackKind !== kind) return null;
  const clip = clips.find((c) => c.id === selectedClip.id);
  if (!clip) return null;
  const st = useTimelineStore.getState();
  const sel = describeSelection({ ...st, playheadTime, selectedClip });
  if (!sel) return null;
  const stop = (e) => e.stopPropagation();
  return (
    <div
      className="tl-clip-pill"
      role="toolbar"
      aria-label={`Acciones de ${sel.label}`}
      style={{ left: clip.start_time * pxPerSec }}
      onMouseDown={stop}
      onClick={stop}
      data-testid="tl-clip-pill"
    >
      <button type="button" title="Reemplazar" aria-label="Reemplazar"
        onClick={() => requestReplace({ trackKind: kind, id: clip.id })} data-testid="tl-pill-replace">
        <ArrowRightLeft size={14} aria-hidden="true" />
      </button>
      {sel.can.editImage && (
        <button type="button" title="Editar imagen" aria-label="Editar imagen"
          onClick={() => requestImageEdit(kind, clip.id)} data-testid="tl-pill-edit-image">
          <PenLine size={14} aria-hidden="true" />
        </button>
      )}
      <button type="button" title={sel.can.cut ? 'Cortar en el cabezal' : 'Poné el cabezal sobre el clip para cortarlo'}
        aria-label="Cortar" disabled={!sel.can.cut} onClick={() => cutSelection(sel)} data-testid="tl-pill-cut">
        <Scissors size={14} aria-hidden="true" />
      </button>
      <button type="button" className="is-danger" title="Borrar" aria-label="Borrar"
        onClick={() => removeSelection(sel)} data-testid="tl-pill-delete">
        <Trash2 size={14} aria-hidden="true" />
      </button>
    </div>
  );
};

// AP-3 · en las pistas base, cada hueco ofrece cerrarse con un toque.
const GapChips = ({ kind, clips }) => {
  const pxPerSec = usePxPerSec();
  const gaps = findGaps(clips);
  if (!gaps.length) return null;
  return gaps.map((g) => {
    const label = `Cerrar espacio de ${g.size.toFixed(1).replace('.', ',')} s`;
    return (
      <button
        key={`${g.start}-${g.end}`}
        type="button"
        className="tl-gap-chip"
        style={{ left: ((g.start + g.end) / 2) * pxPerSec - 12 }}
        title={label}
        aria-label={label}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); useTimelineStore.getState().closeGapAt(kind, g.end, g.size); }}
        data-testid={`tl-gap-${kind}`}
      >
        <MoveHorizontal size={13} aria-hidden="true" />
      </button>
    );
  });
};

const Track = ({ kind, clips, totalWidth, onAdd, onReorder, dropState, laneRef }) => {
  const { selectedClip, selectClip, videoTransitions, addTransition } = useTimelineStore();
  const label =
    kind === 'video' ? 'Video'
    : kind === 'image' ? 'Imagen'
    : kind === 'audio' ? 'Audio'
    : kind === 'v1' ? 'Overlay V1'
    : kind === 'v2' ? 'Overlay V2'
    : kind;
  const Icon =
    kind === 'video' ? Video
    : kind === 'image' ? ImageIcon
    : kind === 'audio' ? Music
    : ImageIcon;

  // For the base video track we look at every pair of *adjacent* clips
  // (i.e. clip A ends exactly where clip B starts) and either show the
  // existing transition or expose a "+" slot to insert one. Only clips
  // that actually touch are eligible — gaps show nothing.
  const isVideoBase = kind === 'video';
  const sorted = isVideoBase
    ? [...clips].sort((a, b) => a.start_time - b.start_time)
    : [];
  const boundaries = [];
  for (let i = 0; i < sorted.length - 1; i += 1) {
    const a = sorted[i];
    const b = sorted[i + 1];
    if (Math.abs((a.start_time + a.duration) - b.start_time) < 0.02) {
      const existing = videoTransitions.find(
        (t) => t.before_id === a.id && t.after_id === b.id
      );
      boundaries.push({ a, b, transition: existing });
    }
  }

  const dropClass = dropState === 'ok' ? ' tl-track-drop-ok'
    : dropState === 'no' ? ' tl-track-drop-no' : '';

  return (
    <div
      className={`tl-track tl-track-${kind}${dropClass}`}
      data-testid={`tl-track-${kind}`}
      ref={laneRef}
      data-track={kind}
    >
      <div
        className="tl-track-label"
        onMouseDown={(e) => onReorder?.(e, kind)}
        title="Arrastrá para subir o bajar la capa (arriba = encima)"
      >
        <GripVertical size={11} className="tl-track-grip" />
        <Icon size={13} />
        <span>{label}</span>
        <button
          type="button"
          className="tl-track-add-btn"
          onClick={(e) => { e.stopPropagation(); onAdd?.(kind); }}
          aria-label={`Agregar contenido a ${label}`}
          data-testid={`tl-track-add-${kind}`}
          data-tooltip="Agregar contenido al timeline"
        >
          <Plus size={10} strokeWidth={2.5} />
        </button>
        <TrackAudioControls trackKind={kind} />
      </div>
      <div className="tl-track-lane" style={{ width: totalWidth }}>
        {clips.map((c) => (
          <ClipBlock
            key={c.id}
            clip={c}
            trackKind={kind}
            isSelected={selectedClip?.id === c.id}
            onClick={() => selectClip({ trackKind: kind, id: c.id })}
          />
        ))}
        {LAYOUT_V2 && <ClipPill kind={kind} clips={clips} />}
        {LAYOUT_V2 && isSequential(kind) && <GapChips kind={kind} clips={clips} />}
        {isVideoBase && boundaries.map(({ a, b, transition }) => (
          <TransitionMarker
            key={transition ? `t-${transition.id}` : `slot-${a.id}-${b.id}`}
            leftClip={a}
            rightClip={b}
            transition={transition}
            isSelected={selectedClip?.trackKind === 'transition' && selectedClip?.id === transition?.id}
            onSelect={(id) => selectClip({ trackKind: 'transition', id })}
            onCreate={(beforeId, afterId) => addTransition({ before_id: beforeId, after_id: afterId })}
          />
        ))}
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────
// Transition inspector — full-width strip that shows below the timeline
// when a fade-to-black transition is selected. Two modes:
//   • Symmetric: single slider for total duration (0.5s each side)
//   • Custom:    two sliders for out (clip A tail) and in (clip B head)
// ────────────────────────────────────────────────────────────────
const TransitionInspector = ({ transition }) => {
  const { updateTransition, removeTransition, videoClips } = useTimelineStore();
  if (!transition) return null;
  const before = videoClips.find((c) => c.id === transition.before_id);
  const after  = videoClips.find((c) => c.id === transition.after_id);
  // Hide the inspector when the two referenced clips are no longer adjacent —
  // the chip on the timeline also disappears in that case, so keeping the
  // inspector open would leave the user editing an invisible transition.
  const stillAdjacent = before && after &&
    Math.abs((before.start_time + before.duration) - after.start_time) < 0.02;
  if (!stillAdjacent) return null;

  const setSymmetric = (sym) => updateTransition(transition.id, { symmetric: sym });
  const setDuration = (d) => updateTransition(transition.id, { duration: d });
  const setOut = (d) => updateTransition(transition.id, { out_duration: d });
  const setIn  = (d) => updateTransition(transition.id, { in_duration: d });

  return (
    <div className="tl-inspector tl-inspector-transition" data-testid="tl-transition-inspector">
      <div className="tl-inspector-header">
        <div className="tl-inspector-title">
          <Waves size={14} />
          <span>Transición · Fundido a negro</span>
          {before && after && (
            <span className="tl-inspector-hint">
              {before.label || 'Clip A'} → {after.label || 'Clip B'}
            </span>
          )}
        </div>
        <button
          className="tl-inspector-remove"
          onClick={() => removeTransition(transition.id)}
          data-testid="tl-transition-remove"
          title="Quitar transición"
        >
          <Trash2 size={12} /> Quitar
        </button>
      </div>

      <div className="tl-inspector-body">
        <div className="tl-transition-mode-toggle" role="tablist">
          <button
            type="button"
            role="tab"
            className={transition.symmetric ? 'active' : ''}
            onClick={() => setSymmetric(true)}
            data-testid="tl-transition-mode-symmetric"
          >
            <Sliders size={11} /> Simétrica
          </button>
          <button
            type="button"
            role="tab"
            className={!transition.symmetric ? 'active' : ''}
            onClick={() => setSymmetric(false)}
            data-testid="tl-transition-mode-custom"
          >
            Personalizada
          </button>
        </div>

        {transition.symmetric ? (
          <label className="tl-inspector-field wide">
            <span>Duración total</span>
            <input
              type="number"
              step="0.1"
              min="0.1"
              max="10"
              value={transition.duration}
              onChange={(e) => setDuration(parseFloat(e.target.value) || 0)}
              data-testid="tl-transition-duration"
            />
            <em className="tl-inspector-help">
              {(transition.duration / 2).toFixed(2)}s salida + {(transition.duration / 2).toFixed(2)}s entrada
            </em>
          </label>
        ) : (
          <>
            <label className="tl-inspector-field">
              <span>Salida del video 1</span>
              <input
                type="number"
                step="0.1"
                min="0"
                max="10"
                value={transition.out_duration}
                onChange={(e) => setOut(parseFloat(e.target.value) || 0)}
                data-testid="tl-transition-out"
              />
            </label>
            <label className="tl-inspector-field">
              <span>Entrada del video 2</span>
              <input
                type="number"
                step="0.1"
                min="0"
                max="10"
                value={transition.in_duration}
                onChange={(e) => setIn(parseFloat(e.target.value) || 0)}
                data-testid="tl-transition-in"
              />
            </label>
            <div className="tl-inspector-field">
              <span>Total</span>
              <strong>{(transition.out_duration + transition.in_duration).toFixed(2)}s</strong>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────
// Clip inspector
// ────────────────────────────────────────────────────────────────
const ClipInspector = ({ clip, trackKind }) => {
  const { updateClip, updateTransform, removeClip, splitClip, playheadTime } = useTimelineStore();
  if (!clip) return null;
  const isAudio = trackKind === 'audio';
  const isOverlay = trackKind === 'v1' || trackKind === 'v2';

  const field = (key, label, props = {}) => (
    <label className="tl-inspector-field">
      <span>{label}</span>
      <input
        type="number"
        value={clip[key] ?? 0}
        onChange={(e) => updateClip(trackKind, clip.id, { [key]: parseFloat(e.target.value) || 0 })}
        step={props.step ?? 0.1}
        min={props.min ?? 0}
        max={props.max}
        data-testid={`tl-field-${key}`}
      />
    </label>
  );

  return (
    <div className="tl-inspector" data-testid="tl-inspector">
      <div className="tl-inspector-header">
        <span className="tl-inspector-title">
          {isAudio ? 'Audio' : (clip.kind === 'image' ? 'Imagen' : 'Video')} — {clip.label}
        </span>
        <div className="tl-inspector-actions">
          {clip.source?.node_id && (
            <button
              className="tl-icon-btn"
              onClick={() => goToSourceNode(clip.source.node_id)}
              title={`Ir al nodo de origen (#${String(clip.source.node_id).replace(/\D/g, '')})`}
              data-testid="tl-goto-node-btn"
            >
              <Crosshair size={14} />
            </button>
          )}
          <button className="tl-icon-btn" onClick={() => splitClip(trackKind, clip.id, playheadTime)} title="Cortar en playhead" data-testid="tl-cut-btn">
            <Scissors size={14} />
          </button>
          <button className="tl-icon-btn danger" onClick={() => removeClip(trackKind, clip.id)} title="Eliminar" data-testid="tl-delete-btn">
            <Trash2 size={14} />
          </button>
        </div>
      </div>
      <div className="tl-inspector-grid">
        {field('start_time', 'Inicio (s)')}
        {field('duration', 'Duración (s)', { min: 0.1 })}
        {field('trim_start', 'Trim inicio (s)')}
        {field('fade_in', 'Fade in (s)')}
        {field('fade_out', 'Fade out (s)')}
        {isAudio && (
          <>
            {field('volume', 'Volumen', { step: 0.05, min: 0, max: 2 })}
            <label className="tl-inspector-field tl-inspector-toggle">
              <span>Loop</span>
              <input
                type="checkbox"
                checked={!!clip.loop}
                onChange={(e) => updateClip('audio', clip.id, { loop: e.target.checked })}
                data-testid="tl-field-loop"
              />
            </label>
            <label className="tl-inspector-field tl-inspector-toggle">
              <span>Mute</span>
              <input
                type="checkbox"
                checked={!!clip.muted}
                onChange={(e) => updateClip('audio', clip.id, { muted: e.target.checked })}
                data-testid="tl-field-muted"
              />
            </label>
          </>
        )}
        {!isAudio && clip.kind === 'video' && (
          <>
            <label className="tl-inspector-field tl-inspector-toggle">
              <span>Audio original</span>
              <input
                type="checkbox"
                checked={clip.source_audio_enabled !== false}
                onChange={(e) => updateClip(trackKind, clip.id, { source_audio_enabled: e.target.checked })}
                data-testid="tl-field-source-audio"
              />
            </label>
            <label className="tl-inspector-field tl-inspector-toggle">
              <span>Mute</span>
              <input
                type="checkbox"
                checked={!!clip.muted}
                onChange={(e) => updateClip(trackKind, clip.id, { muted: e.target.checked })}
                data-testid="tl-field-muted"
              />
            </label>
            {field('volume', 'Volumen', { step: 0.05, min: 0, max: 2 })}
            {field('audio_fade_in', 'Fade audio in (s)')}
            {field('audio_fade_out', 'Fade audio out (s)')}
          </>
        )}
      </div>

      {isOverlay && (
        <div className="tl-inspector-transform" data-testid="tl-inspector-transform">
          <div className="tl-inspector-subhead">Transform (arrastrá en el preview)</div>
          <div className="tl-inspector-grid">
            <TransformField label="Pos X (%)" val={clip.transform?.x_pct ?? 0.5} mult={100} step={1}
              onChange={(v) => updateTransform(trackKind, clip.id, { x_pct: v / 100 })} />
            <TransformField label="Pos Y (%)" val={clip.transform?.y_pct ?? 0.5} mult={100} step={1}
              onChange={(v) => updateTransform(trackKind, clip.id, { y_pct: v / 100 })} />
            <TransformField label="Escala (%)" val={clip.transform?.scale ?? 0.4} mult={100} step={1} min={5} max={250}
              onChange={(v) => updateTransform(trackKind, clip.id, { scale: v / 100 })} />
            <TransformField label="Rotación (°)" val={clip.transform?.rotation ?? 0} mult={1} step={1} min={-360} max={360}
              onChange={(v) => updateTransform(trackKind, clip.id, { rotation: v })} />
            <TransformField label="Opacidad (%)" val={clip.transform?.opacity ?? 1} mult={100} step={1} min={0} max={100}
              onChange={(v) => updateTransform(trackKind, clip.id, { opacity: v / 100 })} />
            <TransformField label="Borde redondeado" val={clip.transform?.corner_radius ?? 0} mult={100} step={1} min={0} max={50}
              onChange={(v) => updateTransform(trackKind, clip.id, { corner_radius: v / 100 })} />
          </div>
        </div>
      )}
    </div>
  );
};

const TransformField = ({ label, val, mult, step, min = 0, max = 100, onChange }) => (
  <label className="tl-inspector-field">
    <span>{label}</span>
    <input
      type="number"
      value={Math.round((val ?? 0) * mult * 10) / 10}
      step={step}
      min={min}
      max={max}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      data-testid={`tl-tfield-${label.split(' ')[0].toLowerCase()}`}
    />
  </label>
);

// ────────────────────────────────────────────────────────────────
// OverlayInPreview — render a single overlay clip over the main
// preview, with drag-to-move, corner resize and rotation when the
// clip is the current selection. All values are normalized 0..1.
// ────────────────────────────────────────────────────────────────
const OverlayInPreview = ({ clip, trackKind, isSelected, onSelect, onChange, playheadTime, layerZ = 2 }) => {
  const t = clip.transform || {};
  const xp = t.x_pct ?? 0.5;
  const yp = t.y_pct ?? 0.5;
  const sc = t.scale ?? 0.4;
  const rot = t.rotation ?? 0;
  const op  = t.opacity ?? 1;
  const radius = (t.corner_radius ?? 0) * 50; // % of min(w,h)

  const rootRef = useRef(null);
  const videoRef = useRef(null);
  const [drag, setDrag] = useState(null);      // { mode, startX, startY, startState }

  // Sync <video> to playhead
  const gain = useTimelineStore((s) => s.clipGain(trackKind, clip));
  useEffect(() => {
    const el = videoRef.current;
    if (!el || clip.kind !== 'video') return;
    const target = clampPreviewTime(clip, playheadTime);
    if (Math.abs((el.currentTime || 0) - target) > 0.25) {
      try { el.currentTime = Math.max(0, target); } catch (_) {}
    }
    // El overlay suena si el modelo de audio lo permite (mismo cálculo que
    // usa la exportación): mute/solo/volumen de clip, pista y master.
    el.muted = gain <= 0;
    el.volume = previewVolume(gain);
    if (el.paused) el.play().catch(() => {});
  }, [clip.kind, clip.start_time, clip.trim_start, playheadTime, clip.source_url, gain]);

  const startDrag = (e, mode) => {
    e.preventDefault(); e.stopPropagation();
    onSelect();
    const container = rootRef.current?.parentElement;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    setDrag({
      mode,
      containerW: rect.width,
      containerH: rect.height,
      startX: e.clientX,
      startY: e.clientY,
      start: { xp, yp, sc, rot },
    });
  };

  useEffect(() => {
    if (!drag) return;
    const onMove = (e) => {
      const dx = (e.clientX - drag.startX) / drag.containerW;
      const dy = (e.clientY - drag.startY) / drag.containerH;
      if (drag.mode === 'move') {
        onChange({
          x_pct: clamp(drag.start.xp + dx, 0, 1),
          y_pct: clamp(drag.start.yp + dy, 0, 1),
        });
      } else if (drag.mode.startsWith('resize-')) {
        // Use diagonal distance; positive = grow
        const factor = Math.hypot(dx, dy) * (dx + dy > 0 ? 1 : -1);
        onChange({ scale: clamp(drag.start.sc + factor * 1.2, 0.05, 2.5) });
      } else if (drag.mode === 'rotate') {
        // The rotation handle sits at -40px above center — compute angle around center
        const container = rootRef.current?.parentElement;
        const rect = container.getBoundingClientRect();
        const cx = rect.left + drag.start.xp * rect.width;
        const cy = rect.top + drag.start.yp * rect.height;
        const angle = Math.atan2(e.clientY - cy, e.clientX - cx) * 180 / Math.PI + 90;
        onChange({ rotation: Math.round(angle) });
      }
    };
    const onUp = () => setDrag(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [drag, onChange]);

  // Box size: scale is a fraction of container WIDTH; keep aspect via CSS (auto height)
  const boxStyle = {
    position: 'absolute',
    left: `${xp * 100}%`,
    top:  `${yp * 100}%`,
    width: `${sc * 100}%`,
    transform: `translate(-50%, -50%) rotate(${rot}deg)`,
    opacity: op,
    borderRadius: `${radius}%`,
    cursor: isSelected ? 'move' : 'pointer',
    zIndex: layerZ,
  };

  return (
    <div
      ref={rootRef}
      className={`tl-overlay ${isSelected ? 'is-selected' : ''} track-${trackKind}`}
      style={boxStyle}
      onMouseDown={(e) => isSelected ? startDrag(e, 'move') : (e.stopPropagation(), onSelect())}
      data-testid={`tl-overlay-${clip.id}`}
    >
      {clip.kind === 'image' ? (
        <img src={clip.source_url} alt="" className="tl-overlay-media" draggable={false} />
      ) : (
        <video
          ref={videoRef}
          src={clip.source_url}
          className="tl-overlay-media"
          playsInline
          muted={gain <= 0}
        />
      )}
      {isSelected && (
        <>
          <div className="tl-overlay-handle rotate" onMouseDown={(e) => startDrag(e, 'rotate')} title="Rotar" />
          <div className="tl-overlay-handle nw" onMouseDown={(e) => startDrag(e, 'resize-nw')} />
          <div className="tl-overlay-handle ne" onMouseDown={(e) => startDrag(e, 'resize-ne')} />
          <div className="tl-overlay-handle sw" onMouseDown={(e) => startDrag(e, 'resize-sw')} />
          <div className="tl-overlay-handle se" onMouseDown={(e) => startDrag(e, 'resize-se')} />
          <div className="tl-overlay-label">{trackKind.toUpperCase()} · {Math.round(sc * 100)}%</div>
        </>
      )}
    </div>
  );
};

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

// ────────────────────────────────────────────────────────────────
// Master Preview: renders the composition live while playing.
// - Topmost video/image clip active at current time is shown
// - Active audio clips play synchronously with correct offsets
// ────────────────────────────────────────────────────────────────
const MasterPreview = ({ playheadTime, isPlaying }) => {
  // z-index del apilado desde `trackOrder`: la pista de arriba en el editor
  // se dibuja encima, igual que en el MP4 exportado.
  const trackOrder = useTimelineStore((s) => s.trackOrder);
  const layerZ = useCallback((track) => {
    const idx = (trackOrder || []).indexOf(track);
    return idx < 0 ? 1 : (trackOrder.length - idx);
  }, [trackOrder]);
  const {
    videoClips, imageClips, audioClips, overlayV1Clips, overlayV2Clips,
    selectedClip, selectClip, updateTransform,
    setPlaying, setPlayhead, getTotalDuration,
  } = useTimelineStore();
  const elements = useTimelineStore((s) => s.elements);
  const updateElement = useTimelineStore((s) => s.updateElement);
  // AP-4 · el clip que se está editando se dibuja como caja de edición (aunque el cabezal se mueva).
  const imageEdit = useTimelineStore((s) => s.imageEdit);
  const editClip = useTimelineStore((s) => findEditClip(s, s.imageEdit));
  const totalDuration = getTotalDuration();

  // Elementos estáticos activos (texto y stickers), ordenados por capa.
  const stageRef = useRef(null);
  const [stage, setStage] = useState({ width: 640, height: 360 });

  useEffect(() => {
    const el = stageRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => setStage({ width: el.clientWidth, height: el.clientHeight });
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    measure();
    return () => ro.disconnect();
  }, []);

  const activeElements = useMemo(
    () => elements
      .filter((el) => el.visible && playheadTime >= el.start
        && playheadTime < el.start + el.duration)
      .sort((a, b) => a.z_order - b.z_order),
    [elements, playheadTime]
  );

  // Find active visual clip (latest start_time wins if overlap)
  const activeVisual = useMemo(() => {
    const candidates = [...videoClips, ...imageClips]
      .filter((c) => playheadTime >= c.start_time && playheadTime < c.start_time + c.duration)
      .sort((a, b) => b.start_time - a.start_time);
    return candidates[0] || null;
  }, [videoClips, imageClips, playheadTime]);

  // Overlays active at the current playhead (v1 below v2)
  const activeOverlays = useMemo(() => {
    const pick = (list, trackKind) =>
      list.filter((c) => playheadTime >= c.start_time && playheadTime < c.start_time + c.duration)
          .map((c) => ({ clip: c, trackKind }));
    return [...pick(overlayV1Clips, 'v1'), ...pick(overlayV2Clips, 'v2')];
  }, [overlayV1Clips, overlayV2Clips, playheadTime]);

  const videoRef = useRef(null);
  const audioRefs = useRef({}); // id → HTMLAudioElement

  // Sync the main <video> to the right source & time when visual changes
  const clipGain = useTimelineStore((s) => s.clipGain);
  const baseGain = useTimelineStore((s) => (
    activeVisual ? s.clipGain('video', activeVisual) : 0
  ));
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !activeVisual || activeVisual.kind !== 'video') return;
    if (el.src !== activeVisual.source_url) el.src = activeVisual.source_url;
    const target = clampPreviewTime(activeVisual, playheadTime);
    if (Math.abs((el.currentTime || 0) - target) > 0.2) {
      try { el.currentTime = Math.max(0, target); } catch (_) {}
    }
    // Audio embebido del clip base con la misma ganancia que la exportación.
    el.muted = baseGain <= 0;
    el.volume = previewVolume(baseGain);
    if (isPlaying && el.paused) el.play().catch(() => {});
    if (!isPlaying && !el.paused) el.pause();
  }, [activeVisual, playheadTime, isPlaying, baseGain]);

  // Manage audio elements per clip
  useEffect(() => {
    const active = audioClips.filter(
      (c) => playheadTime >= c.start_time && playheadTime < c.start_time + c.duration
    );
    const activeIds = new Set(active.map((c) => c.id));

    // Pause & remove outdated
    Object.entries(audioRefs.current).forEach(([id, el]) => {
      if (!activeIds.has(id)) { try { el.pause(); } catch (_) {} }
    });

    active.forEach((c) => {
      let el = audioRefs.current[c.id];
      if (!el) {
        el = new Audio();
        // NO crossOrigin — plain <audio> playback works fine in no-cors mode.
        // Setting crossOrigin='anonymous' makes the browser require an
        // Access-Control-Allow-Origin header from R2 and old assets in the
        // bucket fail this preflight, leaving the audio in a broken state.
        el.preload = 'auto';
        el.src = c.source_url;
        el.loop = !!c.loop;
        el.addEventListener('error', (ev) => {
          console.warn('[timeline] audio failed to load', c.source_url, ev);
        });
        audioRefs.current[c.id] = el;
      }
      el.volume = previewVolume(clipGain('audio', c));
      el.muted = clipGain('audio', c) <= 0;
      const target = (c.trim_start || 0) + (playheadTime - c.start_time);
      if (Math.abs((el.currentTime || 0) - target) > 0.25) {
        try { el.currentTime = Math.max(0, target); } catch (_) {}
      }
      if (isPlaying && el.paused) el.play().catch(() => {});
      if (!isPlaying && !el.paused) el.pause();
    });
  }, [audioClips, playheadTime, isPlaying, clipGain]);

  // Cleanup all audio on unmount
  useEffect(() => () => {
    Object.values(audioRefs.current).forEach((el) => { try { el.pause(); } catch (_) {} });
    audioRefs.current = {};
  }, []);

  // RAF transport
  useEffect(() => {
    if (!isPlaying) return undefined;
    let raf;
    let startedWallClock = performance.now();
    let startedAtPlayhead = playheadTime;
    const tick = () => {
      const elapsed = (performance.now() - startedWallClock) / 1000;
      const next = startedAtPlayhead + elapsed;
      if (next >= totalDuration) {
        setPlayhead(totalDuration);
        setPlaying(false);
        return;
      }
      setPlayhead(next);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
    // intentionally not depending on playheadTime — it would cancel the RAF every frame.
    // eslint-disable-next-line
  }, [isPlaying, totalDuration]);

  const togglePlay = () => {
    if (totalDuration <= 0) return;
    if (!isPlaying && playheadTime >= totalDuration - 0.05) setPlayhead(0);
    setPlaying(!isPlaying);
  };

  return (
    <>
    <div className={`tl-preview${editClip ? ' is-image-editing' : ''}`} data-testid="tl-preview" ref={stageRef}>
      {!activeVisual && activeOverlays.length === 0 && (
        <div className="tl-preview-empty-hint">Sin contenido en {formatTime(playheadTime)}</div>
      )}
      {activeVisual?.kind === 'image' && activeVisual.id !== editClip?.id && (
        <img src={activeVisual.source_url} alt="" className="tl-preview-media"
          style={{ zIndex: layerZ('image') }} />
      )}
      {activeVisual?.kind === 'video' && (
        <video ref={videoRef} muted={false} className="tl-preview-media" playsInline
          style={{ zIndex: layerZ('video') }} data-testid="tl-master-video" />
      )}

      {/* Overlay layer: positioned clips, draggable/resizable when selected */}
      {activeOverlays.filter(({ clip }) => clip.id !== editClip?.id).map(({ clip, trackKind }) => (
        <OverlayInPreview
          key={clip.id}
          clip={clip}
          trackKind={trackKind}
          isSelected={selectedClip?.id === clip.id && selectedClip?.trackKind === trackKind}
          onSelect={() => selectClip({ trackKind, id: clip.id })}
          onChange={(patch) => updateTransform(trackKind, clip.id, patch)}
          playheadTime={playheadTime}
          layerZ={layerZ(trackKind)}
        />
      ))}

      {editClip && (
        <TimelineImageEditBox clip={editClip} trackKind={imageEdit.trackKind} stage={stage} />
      )}

      {/* Elementos estáticos: texto y stickers, arrastrables en el preview */}
      {activeElements.map((el) => (
        <ElementInPreview
          key={el.element_id}
          element={el}
          layerZ={layerZ(el.type)}
          canvasWidth={stage.width}
          canvasHeight={stage.height}
          isSelected={selectedClip?.id === el.element_id}
          onSelect={() => selectClip({ trackKind: el.type, id: el.element_id })}
          onChange={(patch) => updateElement(el.element_id, {
            ...(patch.transform ? { transform: { ...el.transform, ...patch.transform } } : {}),
            ...(patch.style ? { style: { ...el.style, ...patch.style } } : {}),
          })}
        />
      ))}

    </div>

    {/* Transporte FUERA del preview: antes flotaba encima (z-index 50) y
        tapaba la franja de abajo, donde suelen ir subtítulos y stickers. */}
    <div className="tl-preview-transport" data-testid="tl-transport">
      <button
        className="tl-preview-playbtn master"
        onClick={togglePlay}
        data-testid="tl-master-play"
        disabled={totalDuration <= 0}
      >
        {isPlaying ? <Pause size={16} /> : <Play size={16} />}
      </button>
      <div className="tl-preview-time">
        <span>{formatTime(playheadTime)}</span>
        <span className="tl-preview-time-sep">/</span>
        <span>{formatTime(totalDuration)}</span>
      </div>
    </div>
    </>
  );
};

// ────────────────────────────────────────────────────────────────
// Gallery picker modal
// ────────────────────────────────────────────────────────────────
const GalleryPicker = ({ targetTrack, onClose, onPick }) => {
  const [assets, setAssets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(
    targetTrack === 'audio' ? 'audio' : targetTrack === 'image' ? 'image' : 'video'
  );

  // Misma fuente y mismo filtro que la galería principal: el backend
  // devuelve `media_kind` real (mime `video/*`), nombre derivado y thumbnail.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    galleryService.listAssets({ assetType: tab, limit: 60 })
      .then((data) => { if (!cancelled) setAssets(data.assets || []); })
      .catch(() => { if (!cancelled) setAssets([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tab]);

  return (
    <div className="tl-gallery-overlay" onClick={onClose} data-testid="tl-gallery-overlay">
      <div className="tl-gallery-modal" onClick={(e) => e.stopPropagation()}>
        <div className="tl-gallery-header">
          <span className="tl-gallery-title">Galería — elegí un asset</span>
          <div className="tl-gallery-tabs">
            {['video', 'image', 'audio'].map((t) => (
              <button key={t} className={`tl-gallery-tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)} data-testid={`tl-gallery-tab-${t}`}>
                {t === 'video' ? 'Videos' : t === 'image' ? 'Imágenes' : 'Audios'}
              </button>
            ))}
          </div>
          <button className="tl-icon-btn" onClick={onClose} data-testid="tl-gallery-close"><X size={14} /></button>
        </div>
        <div className="tl-gallery-grid">
          {loading && <div className="tl-gallery-empty"><Loader2 size={18} className="spin" /></div>}
          {!loading && assets?.length === 0 && (
            <div className="tl-gallery-empty">No hay assets. Generá algo o subí un archivo.</div>
          )}
          {!loading && assets?.map((a) => {
            const kind = a.media_kind || a.asset_type;
            return (
              <button key={a.id} className="tl-gallery-card"
                onClick={() => onPick(a)}
                data-testid={`tl-gallery-card-${a.id}`}
              >
                {kind === 'image' && <img src={a.url} alt={a.filename} loading="lazy" />}
                {kind === 'video' && (
                  a.thumbnail_url
                    ? <img src={a.thumbnail_url} alt={a.filename} loading="lazy" />
                    : <div className="tl-gallery-audio-icon"><Film size={28} /></div>
                )}
                {kind === 'audio' && <div className="tl-gallery-audio-icon"><Music size={28} /></div>}
                <span className="tl-gallery-filename">{a.filename}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────
// Main Editor
// ────────────────────────────────────────────────────────────────
const TL_HEIGHT_KEY = 'medialabs.timeline.height';
const TL_HEIGHT_MIN = 320;
const TL_HEIGHT_MAX_RATIO = 0.92; // cap at 92% of viewport
const TL_HEIGHT_DEFAULT = Math.round(window.innerHeight * 0.46);

const TimelineEditor = () => {
  const {
    isOpen, closeEditor, videoClips, imageClips, audioClips, overlayV1Clips, overlayV2Clips,
    selectedClip, resolution, setResolution, playheadTime, setPlayhead, isPlaying,
    addVideoClip, addAudioClip, clearTimeline, getTotalDuration,
    renderJob, setRenderJob,
  } = useTimelineStore();

  const [gallerySel, setGallerySel] = useState(null);
  const [uploading, setUploading] = useState(false);
  // AP-3 · «Reemplazar» ({ trackKind, id }) y tope de subida del plan
  const [replaceTarget, setReplaceTarget] = useState(null);
  const uploadLimits = useUploadLimits();
  const snapGuide = useTimelineStore((s) => s.snapGuide);
  useEffect(() => {
    const onReplace = (e) => { if (e.detail?.id) setReplaceTarget(e.detail); };
    window.addEventListener('sml:timeline-replace', onReplace);
    return () => window.removeEventListener('sml:timeline-replace', onReplace);
  }, []);
  const [textMenuOpen, setTextMenuOpen] = useState(false);
  const [stickerOpen, setStickerOpen] = useState(false);
  // UI-3 · panel contextual: 'marca' | null (la selección de un clip/texto lo reemplaza)
  const [sidePanel, setSidePanel] = useState(null);
  const imageEditTarget = useTimelineStore((s) => s.imageEdit);
  // E3 · plantillas
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const fileInputRef = useRef(null);
  const elements = useTimelineStore((s) => s.elements);
  const addTextElement = useTimelineStore((s) => s.addTextElement);
  const addStickerElement = useTimelineStore((s) => s.addStickerElement);
  const serializeTimeline = useTimelineStore((s) => s.serializeTimeline);
  const hydrateTimeline = useTimelineStore((s) => s.hydrateTimeline);
  const currentProjectId = useWorkflowStore((s) => s.currentProjectId);

  // ── Escala y ancho navegable ────────────────────────────────────
  // El ancho del lienzo del timeline sigue al contenido: el último clip
  // (en cualquier pista, esté donde esté) más un colchón de cola, y nunca
  // menos que el viewport. Regla, pistas, clips y playhead usan la MISMA
  // escala (`pxPerSec`), así que el scroll horizontal llega siempre al final.
  const pxPerSec = usePxPerSec();
  const zoom = useTimelineStore((s) => s.zoom);
  const zoomIn = useTimelineStore((s) => s.zoomIn);
  const zoomOut = useTimelineStore((s) => s.zoomOut);
  const resetZoom = useTimelineStore((s) => s.resetZoom);
  const scrollRef = useRef(null);
  const [viewportWidth, setViewportWidth] = useState(800);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setViewportWidth(el.clientWidth));
    ro.observe(el);
    setViewportWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  // ── Resizable editor height (drag top edge to expand/contract) ──
  // Envío UI-2 · tamaño del preview en la Vista Edición (Chico / Medio / Grande).
  const [previewSize, setPreviewSize] = useState(() => {
    try { return ['s', 'm', 'l'].includes(localStorage.getItem('sml.v2.previewSize')) ? localStorage.getItem('sml.v2.previewSize') : 'm'; } catch { return 'm'; }
  });
  const pickPreviewSize = useCallback((k) => {
    setPreviewSize(k);
    try { localStorage.setItem('sml.v2.previewSize', k); } catch { /* privado */ }
  }, []);
  const [editorHeight, setEditorHeight] = useState(() => {
    const stored = parseInt(localStorage.getItem(TL_HEIGHT_KEY), 10);
    if (Number.isFinite(stored) && stored >= TL_HEIGHT_MIN) return stored;
    return TL_HEIGHT_DEFAULT;
  });
  const resizeDragRef = useRef(null);

  const startResize = (e) => {
    e.preventDefault();
    resizeDragRef.current = {
      startY: e.clientY,
      startH: editorHeight,
    };
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onResize);
    window.addEventListener('mouseup', endResize);
  };
  const onResize = (e) => {
    const drag = resizeDragRef.current;
    if (!drag) return;
    const max = Math.round(window.innerHeight * TL_HEIGHT_MAX_RATIO);
    // Dragging UP increases height
    const next = Math.min(max, Math.max(TL_HEIGHT_MIN, drag.startH + (drag.startY - e.clientY)));
    setEditorHeight(next);
  };
  const endResize = () => {
    if (!resizeDragRef.current) return;
    resizeDragRef.current = null;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    window.removeEventListener('mousemove', onResize);
    window.removeEventListener('mouseup', endResize);
    // Persist the chosen height
    setEditorHeight((h) => {
      try { localStorage.setItem(TL_HEIGHT_KEY, String(h)); } catch (_e) { /* quota / privacy */ }
      return h;
    });
  };

  const totalDuration = useMemo(
    () => getTotalDuration(),
    [videoClips, imageClips, audioClips, overlayV1Clips, overlayV2Clips, elements, getTotalDuration]
  );
  const totalWidth = Math.max(
    (totalDuration + TIMELINE_TAIL_SECONDS) * pxPerSec,
    viewportWidth - LABEL_GUTTER - 24,
    600
  );
  const cost = calcCost(totalDuration, resolution);

  // Orden de capas: única fuente de verdad (lanes + preview + export).
  const trackOrder = useTimelineStore((s) => s.trackOrder);
  const moveTrack = useTimelineStore((s) => s.moveTrack);
  const clipsByTrack = useMemo(() => ({
    v2: overlayV2Clips, v1: overlayV1Clips,
    video: videoClips, image: imageClips, audio: audioClips,
  }), [overlayV2Clips, overlayV1Clips, videoClips, imageClips, audioClips]);

  /** Arrastre vertical del ENCABEZADO de una pista para subirla o bajarla. */
  const startTrackReorder = useCallback((e, trackId) => {
    if (e.button !== 0 || e.target.closest('button, input')) return;
    e.preventDefault();
    const laneHeight = e.currentTarget.closest('.tl-track')?.offsetHeight || 44;
    const startY = e.clientY;
    let applied = 0;
    const onMove = (ev) => {
      const steps = Math.round((ev.clientY - startY) / laneHeight);
      while (applied < steps) { if (!moveTrack(trackId, 1)) break; applied += 1; }
      while (applied > steps) { if (!moveTrack(trackId, -1)) break; applied -= 1; }
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
    };
    document.body.style.cursor = 'grabbing';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [moveTrack]);

  // E3 · huecos de plantilla pendientes (los avisa la barra y el export).
  const slots = useTimelineStore((s) => s.slots);
  const slotStatus = useMemo(() => ({
    total: (slots || []).length,
    empty: (slots || []).filter((s) => !s.clip_id).length,
  }), [slots]);

  const reloadFromServer = useCallback(async () => {
    if (!currentProjectId) return;
    try {
      const remote = await fetchTimeline(currentProjectId);
      if (remote) {
        hydrateTimeline(remote.data);
        const store = useTimelineStore.getState();
        store.setServerVersion(remote.version);
        store.setSyncStatus('saved');
      }
    } catch (_e) { /* offline: el respaldo local sigue valiendo */ }
  }, [currentProjectId, hydrateTimeline]);

  // ── Persistencia del timeline por proyecto ──────────────────────
  // E2 · TIMELINE_SERVER_SYNC: el servidor es la fuente de verdad y
  // localStorage queda como RESPALDO (se sigue escribiendo y no se borra).
  // Con la flag apagada, esto se comporta igual que antes: sólo localStorage.
  const storageKey = `sml.timeline.project.${currentProjectId || 'sin-proyecto'}`;
  const hydratedKeyRef = useRef(null);
  const syncStatus = useTimelineStore((s) => s.syncStatus);
  const syncConflict = useTimelineStore((s) => s.syncConflict);

  // Firma del documento completo: cualquier cambio (incluidas transiciones,
  // mezcla de audio, fps, slots y orden de capas) dispara el guardado.
  // Misma firma que usa el store para el "sin rebote" (`savedSignature`).
  const docSignature = useTimelineStore((s) => docSignatureOf(s));

  const readLocal = useCallback(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch (_e) { return null; }
  }, [storageKey]);

  useEffect(() => {
    if (hydratedKeyRef.current === storageKey) return;
    hydratedKeyRef.current = storageKey;
    const local = readLocal();
    const tl = useTimelineStore.getState();

    if (!TIMELINE_SERVER_SYNC || !currentProjectId) {
      if (local) hydrateTimeline(local);
      return;
    }

    // Servidor primero; si no tiene nada y hay respaldo local, se sube.
    (async () => {
      try {
        const remote = await fetchTimeline(currentProjectId);
        if (remote) {
          hydrateTimeline(remote.data);
          tl.setServerVersion(remote.version);
          tl.setSyncStatus('saved');
          return;
        }
        if (local) {
          hydrateTimeline(local);
          const out = await migrateTimeline(currentProjectId, local);
          tl.setServerVersion(out.version ?? null);
          tl.setSyncStatus('saved');
        } else {
          // Proyecto sin timeline: arrancar VACÍO. Antes quedaba en memoria el
          // timeline del proyecto anterior y el autoguardado lo escribía en
          // este (así un proyecto nuevo heredaba clips de otro).
          tl.clearTimeline();
          tl.setSyncStatus('idle');
        }
      } catch (_e) {
        // Sin servidor se trabaja offline con el respaldo: sin errores.
        if (local) hydrateTimeline(local);
        tl.setSyncStatus('offline');
      }
    })();
  }, [storageKey, currentProjectId, hydrateTimeline, readLocal]);

  // E6 · Timeline en vivo: al ABRIR el editor, si LMX dejó una versión más
  // nueva en el servidor y no hay cambios locales, hidrata en silencio.
  // Un solo pedido por apertura; sin polling y sin sync con el editor cerrado.
  useEffect(() => {
    if (!isOpen || !TIMELINE_SERVER_SYNC || !currentProjectId) return;
    if (hydratedKeyRef.current !== storageKey) return; // la carga inicial ya trae lo último
    useTimelineStore.getState().syncTimelineFromAgent(currentProjectId);
  }, [isOpen, currentProjectId, storageKey]);

  // Respaldo local (como siempre) + guardado en servidor con debounce ~1 s.
  useEffect(() => {
    if (hydratedKeyRef.current !== storageKey) return undefined;
    const snapshot = serializeTimeline();
    try {
      localStorage.setItem(storageKey, JSON.stringify(snapshot));
    } catch (_e) { /* cuota / modo privado */ }

    if (!TIMELINE_SERVER_SYNC || !currentProjectId) return undefined;
    const tl = useTimelineStore.getState();
    if (tl.syncConflict) return undefined;
    // Sin rebote: lo que acaba de llegar del servidor (hidratación o sync en
    // vivo de LMX) NO se vuelve a subir ni le sube la versión.
    if (docSignature === tl.savedSignature) return undefined;

    const timer = setTimeout(() => {
      useTimelineStore.getState().saveNow(currentProjectId);
    }, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // El autosave sigue al DOCUMENTO COMPLETO (`docSignature`): clips,
    // elementos, transiciones, mezcla de audio, fps, huecos y orden de capas.
  }, [storageKey, currentProjectId, serializeTimeline, docSignature]);

  const selectedElement = (selectedClip?.trackKind === 'text' || selectedClip?.trackKind === 'sticker')
    ? elements.find((el) => el.element_id === selectedClip.id)
    : null;

  const selectedClipObj = selectedClip
    ? (selectedClip.trackKind === 'video' ? videoClips
       : selectedClip.trackKind === 'image' ? imageClips
       : selectedClip.trackKind === 'v1'    ? overlayV1Clips
       : selectedClip.trackKind === 'v2'    ? overlayV2Clips
       : (selectedClip.trackKind === 'transition'
          || selectedClip.trackKind === 'text'
          || selectedClip.trackKind === 'sticker') ? null   // handled separately
       : audioClips
      )?.find((c) => c.id === selectedClip.id)
    : null;

  const videoTransitions = useTimelineStore((s) => s.videoTransitions);
  const selectedTransitionObj = (selectedClip?.trackKind === 'transition')
    ? videoTransitions.find((t) => t.id === selectedClip.id)
    : null;

  // ── Gallery integration
  const handleOpenGallery = (targetTrack) => setGallerySel({ open: true, targetTrack });

  const handleGalleryPick = async (asset) => {
    const targetTrack = gallerySel?.targetTrack;
    setGallerySel(null);
    if (!asset?.id && !asset?.url) return;
    const kind = asset.media_kind || asset.asset_type;
    await sendAssetToTimeline(asset.id, {
      fallbackUrl: asset.url,
      fallbackKind: kind,
      label: asset.filename,
      duration: asset.duration || asset.metadata?.duration || null,
      thumbUrl: asset.thumbnail_url || null,
      // v1 / v2 → overlay track; 'video' o 'image' → pista base
      track: (targetTrack === 'v1' || targetTrack === 'v2') ? targetTrack : 'base',
    });
  };

  const handleLocalUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const kind = file.type.startsWith('video') ? 'video'
      : file.type.startsWith('audio') ? 'audio'
      : file.type.startsWith('image') ? 'image' : null;
    if (!kind) return toast.error('Tipo de archivo no soportado');

    setUploading(true);
    try {
      const duration = await probeMediaDuration(file);
      const { url } = await uploadToR2(file);
      if (kind === 'audio') {
        addAudioClip({ source_url: url, original_duration: duration, label: file.name });
      } else {
        addVideoClip({
          source_url: url, kind, original_duration: duration,
          thumb_url: kind === 'image' ? url : null,
          label: file.name,
        });
      }
      toast.success(`${file.name} agregado`);
    } catch (err) {
      console.error(err);
      toast.error(describeUploadError(err), { duration: 8000 });
    } finally {
      setUploading(false);
    }
  };

  const pollRender = useCallback((execId, costCredits) => {
    try { localStorage.setItem(RENDER_EXEC_STORAGE_KEY, execId); } catch (_e) { /* modo privado */ }
    const poll = async () => {
      try {
        const s = await axios.get(`${API}/timeline/render/${execId}/status`);
        // AP-8 · «completado» sin URL = el MP4 todavía se está guardando
        // (servidores sin el arreglo del backend): se sigue consultando.
        if (s.data.status === 'saving' || (s.data.status === 'completed' && !s.data.output_url)) {
          const prev = useTimelineStore.getState().renderJob;
          setRenderJob({ ...(prev || {}), status: 'saving', execution_id: execId, progress: 99 });
          setTimeout(poll, 1500);
          return;
        }
        if (s.data.status === 'completed') {
          try { localStorage.removeItem(RENDER_EXEC_STORAGE_KEY); } catch (_e) { /* noop */ }
          setRenderJob({
            status: 'completed',
            execution_id: execId,
            output_url: s.data.output_url,
            cost_credits: s.data.credits_used,
            progress: 100,
          });
          toast.success('¡Render completado!');
          if (s.data.audio_degraded) {
            toast.warning(
              'El video se exportó sin el audio de los clips: el renderer remoto necesita actualizarse.',
              { duration: 10000 }
            );
          }
          if (s.data.elements_degraded) {
            toast.warning(
              'El video se exportó sin los textos ni stickers: el renderer remoto necesita actualizarse.',
              { duration: 10000 }
            );
          }
          return;
        }
        if (s.data.status === 'failed' || s.data.status === 'cancelled') {
          try { localStorage.removeItem(RENDER_EXEC_STORAGE_KEY); } catch (_e) { /* noop */ }
          setRenderJob({ status: 'failed', error: s.data.error || 'Error desconocido' });
          toast.error(`Render falló: ${s.data.error || 'error desconocido'}`);
          return;
        }
        const prev = useTimelineStore.getState().renderJob;
        setRenderJob({
          ...(prev || {}),
          status: 'rendering',
          execution_id: execId,
          cost_credits: costCredits ?? prev?.cost_credits,
          progress: typeof s.data.progress === 'number' ? s.data.progress : prev?.progress,
          stage: s.data.stage || prev?.stage,
          provider: s.data.provider || prev?.provider,
          startedAt: prev?.startedAt || Date.now(),
        });
        setTimeout(poll, 1500);
      } catch (_err) { setTimeout(poll, 5000); }
    };
    setTimeout(poll, 1000);
  }, [setRenderJob]);

  // Al recargar la página, el estado autoritativo lo tiene el backend:
  // preguntamos por el render activo y retomamos el progreso y la etapa.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/timeline/renders/active`);
        const active = (res.data?.renders || [])[0];
        if (cancelled) return;
        if (!active) {
          try { localStorage.removeItem(RENDER_EXEC_STORAGE_KEY); } catch (_e) { /* noop */ }
          return;
        }
        setRenderJob({
          status: 'rendering',
          execution_id: active.execution_id,
          cost_credits: active.cost_credits,
          progress: active.progress,
          stage: active.stage,
          provider: active.provider,
          startedAt: active.created_at ? new Date(active.created_at).getTime() : Date.now(),
        });
        pollRender(active.execution_id, active.cost_credits);
      } catch (_e) { /* sin render activo o sin sesión */ }
    })();
    return () => { cancelled = true; };
  }, [pollRender, setRenderJob]);

  const handleExport = useCallback(async () => {
    if (useTimelineStore.getState().getTotalDuration() <= 0) return toast.error('La timeline está vacía');
    if (slotStatus.empty > 0 && !window.confirm(
      `Esta composición tiene ${slotStatus.empty} hueco(s) de plantilla sin completar. `
      + 'Se exportará sin ese contenido. ¿Seguir igual?'
    )) {
      setCompleteOpen(true);
      return;
    }
    setRenderJob({ status: 'queuing', startedAt: Date.now() });
    try {
      // AP-9 · todo sale del estado del editor en este instante (no de listas
      // memorizadas): el MP4 es exactamente lo que se ve.
      const payload = buildRenderPayload(useTimelineStore.getState(), { projectId: currentProjectId });
      const res = await axios.post(`${API}/timeline/render`, payload);
      const execId = res.data.execution_id;
      setRenderJob({
        status: 'rendering',
        execution_id: execId,
        cost_credits: res.data.cost_credits,
        startedAt: Date.now(),
      });
      pollRender(execId, res.data.cost_credits);
    } catch (err) {      const msg = err.response?.data?.detail || err.message || 'Error al exportar';
      toast.error(msg);
      setRenderJob({ status: 'failed', error: msg });
    }
  }, [videoClips, imageClips, audioClips, resolution, totalDuration, setRenderJob,
    pollRender, slotStatus.empty, currentProjectId]);

  // Force-download from a (potentially cross-origin) R2 URL via fetch+blob.
  const handleDownload = useCallback(async () => {
    if (!renderJob?.output_url) {
      // AP-8 · nunca un clic mudo: si falta la URL, se avisa y se vuelve a consultar.
      toast.info('El video todavía se está guardando. En unos segundos se habilita la descarga.');
      if (renderJob?.execution_id) {
        setRenderJob({ ...renderJob, status: 'saving' });
        pollRender(renderJob.execution_id, renderJob.cost_credits);
      }
      return;
    }
    setRenderJob({ ...renderJob, status: 'downloading' });
    try {
      const resp = await fetch(renderJob.output_url);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = brandedFileName(renderJob.output_url, 'video.mp4'); // AP-6
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      setRenderJob({ ...renderJob, status: 'completed' });
      toast.success('Descargado');
    } catch (err) {
      console.error(err);
      toast.error('No se pudo descargar — abriendo en nueva pestaña');
      window.open(renderJob.output_url, '_blank', 'noopener');
      setRenderJob({ ...renderJob, status: 'completed' });
    }
  }, [renderJob, setRenderJob, pollRender]);

  // Marcas de la regla: el paso se adapta al zoom para no amontonar números,
  // y cubren todo el ancho navegable (no sólo la duración del contenido).
  // ── Envío UI-3 (layout nuevo) ──
  // Seleccionar un clip o texto reemplaza al panel Marca.
  useEffect(() => { if (selectedClip) setSidePanel(null); }, [selectedClip]);
  // "Exportar MP4" vive en la barra principal: la barra pide el export por evento
  // y acá se corre el MISMO handleExport de siempre.
  const exportRef = useRef(null);
  exportRef.current = () => {
    const st = useTimelineStore.getState().renderJob?.status;
    if (st === 'queuing' || st === 'rendering') return;
    handleExport();
  };
  // Envío LMX-5 · la guía de LMX abre el panel Marca o las Plantillas.
  useEffect(() => {
    const onPanel = (e) => {
      const name = e.detail?.panel;
      if (name === 'marca') setSidePanel('marca');
      if (name === 'personajes') setSidePanel('personajes');
      if (name === 'plantillas' && TIMELINE_TEMPLATES) setTemplatesOpen(true);
    };
    window.addEventListener('sml:timeline-panel', onPanel);
    return () => window.removeEventListener('sml:timeline-panel', onPanel);
  }, []);
  // AP-4 · el aviso de «imagen nueva guardada» abre la Galería.
  useEffect(() => {
    const onGallery = () => setGallerySel({ open: true, targetTrack: 'video' });
    window.addEventListener('sml:timeline-open-gallery', onGallery);
    return () => window.removeEventListener('sml:timeline-open-gallery', onGallery);
  }, []);
  useEffect(() => {
    if (!LAYOUT_V2) return undefined;
    const onExport = () => exportRef.current?.();
    window.addEventListener('sml:timeline-export', onExport);
    return () => window.removeEventListener('sml:timeline-export', onExport);
  }, []);

  const rulerMarks = useMemo(() => {
    const spanSeconds = totalWidth / pxPerSec;
    const step = pxPerSec >= 60 ? 1 : pxPerSec >= 24 ? 2 : pxPerSec >= 12 ? 5 : 10;
    const marks = [];
    for (let s = 0; s <= spanSeconds; s += step) marks.push(Math.round(s));
    return marks;
  }, [totalWidth, pxPerSec]);

  if (!isOpen) return null;

  const rendering = renderJob?.status === 'rendering' || renderJob?.status === 'queuing';

  // AP-3 · el panel de la derecha mide lo que necesita: a la altura del preview
  // para clips y transiciones; hasta abajo para Marca, Personajes y textos.
  const xMode = LAYOUT_V2 && imageEditTarget ? 'tall' : !LAYOUT_V2 || (!selectedClip && !sidePanel) ? 'none'
    : (!sidePanel && !['text', 'sticker'].includes(selectedClip?.trackKind)) ? 'short' : 'tall';
  // Inspectores (layout clásico) o panel contextual X (layout nuevo).
  const sideContent = (() => {
    // AP-4 · mientras se edita una imagen, la ventana X es el panel de herramientas.
    if (imageEditTarget) {
      return (
        <div className="tl-x tl-x-edit" data-testid="tl-x">
          <TimelineImageEditPanel />
        </div>
      );
    }
    const inspectors = (
      <>
        {selectedClipObj && (
          <ImageEditCard clip={selectedClipObj} trackKind={selectedClip?.trackKind} />
        )}
        {selectedClipObj && (
          <ClipInspector clip={selectedClipObj} trackKind={selectedClip?.trackKind} />
        )}
        {selectedTransitionObj && (
          <TransitionInspector transition={selectedTransitionObj} />
        )}
        {selectedElement && <ElementInspector element={selectedElement} />}
      </>
    );
    if (!LAYOUT_V2) return inspectors;
    // UI-3 · ventana X: panel contextual a lo alto, al lado de LMX.
    if (!selectedClip && !sidePanel) return null;
    const view = sidePanel === 'marca' || sidePanel === 'personajes' ? sidePanel : 'sel';
    const sel = selectedClip ? (X_KINDS[selectedClip.trackKind] || X_KINDS.audio) : null;
    // I-45 · al seleccionar algo en el preview o en las pistas, el panel se
    // ilumina en el color de la selección: así se entiende que se edita acá.
    const selKey = selectedClip ? `${selectedClip.trackKind}:${selectedClip.id}` : '';
    return (
      <aside
        className={`tl-x ${sel && view === 'sel' ? 'is-lit' : ''}`}
        style={sel ? { '--x-accent': sel.color } : undefined}
        data-testid="tl-x"
        aria-label="Panel de configuración"
      >
        {sel && view === 'sel' && <span key={selKey} className="tl-x-glow" aria-hidden="true" />}
        {sel && view === 'sel' && <XFirstHint label={sel.label} />}
        <div className="tl-x-head">
          <div className="tl-x-chips" role="tablist" aria-label="Qué mostrar">
            {sel && (
              <button type="button" role="tab" aria-selected={view === 'sel'}
                className={`tl-x-chip ${view === 'sel' ? 'active' : ''}`} style={{ '--chip': sel.color }}
                onClick={() => setSidePanel(null)} data-testid="tl-x-chip-sel">
                <i aria-hidden="true" />{sel.label}
              </button>
            )}
            <button type="button" role="tab" aria-selected={view === 'marca'}
              className={`tl-x-chip ${view === 'marca' ? 'active' : ''}`} style={{ '--chip': '#fbbf24' }}
              onClick={() => setSidePanel('marca')} data-testid="tl-x-chip-brand">
              <i aria-hidden="true" />Marca
            </button>
            <button type="button" role="tab" aria-selected={view === 'personajes'}
              className={`tl-x-chip ${view === 'personajes' ? 'active' : ''}`} style={{ '--chip': '#a78bfa' }}
              onClick={() => setSidePanel('personajes')} data-testid="tl-x-chip-characters">
              <i aria-hidden="true" />Personajes
            </button>
          </div>
          <button
            type="button"
            className="tl-x-close"
            onClick={() => { setSidePanel(null); useTimelineStore.getState().selectClip(null); }}
            title="Cerrar panel"
            aria-label="Cerrar panel"
            data-testid="tl-side-close"
          >
            <X size={15} />
          </button>
        </div>
        <div className="tl-x-body">
          {view === 'marca' ? <BrandPanel /> : view === 'personajes' ? <CharacterPanel /> : (inspectors.props.children.some(Boolean)
            ? inspectors : <p className="tl-x-empty">Seleccioná un clip o un texto del timeline para ver sus opciones.</p>)}
        </div>
      </aside>
    );
  })();

  return (
    <div
      className={`tl-editor ${LAYOUT_V2 ? 'tl-v2' : ''}`}
      data-testid="timeline-editor"
      data-preview-size={LAYOUT_V2 ? previewSize : undefined}
      style={LAYOUT_V2 ? undefined : { height: editorHeight }}
    >
      {/* Resize handle — drag up/down to resize the editor (en UI-2 el editor
          ocupa todo el escenario: no hay borde que arrastrar) */}
      {!LAYOUT_V2 && (
      <div
        className="tl-resize-handle"
        onMouseDown={startResize}
        data-testid="tl-resize-handle"
        title="Arrastrá para cambiar la altura"
        role="separator"
        aria-orientation="horizontal"
        aria-label="Redimensionar timeline"
      >
        <div className="tl-resize-grip" />
      </div>
      )}

      {/* AP-3 · en el layout nuevo el encabezado baja a la barra del timeline */}
      {!LAYOUT_V2 && (
      <div className="tl-editor-header">
        <div className="tl-editor-title">
          <Video size={16} />
          <span>Timeline Editor</span>
          <span className="tl-editor-duration">
            {formatTime(totalDuration)} · {resolution.toUpperCase()}
          </span>
        </div>

        <div className="tl-editor-controls">
          <MasterAudioControls />

          <div className="tl-zoom-group" data-testid="tl-zoom-group">
            <button
              type="button"
              className="tl-zoom-btn"
              onClick={zoomOut}
              title="Alejar (menos px por segundo)"
              data-testid="tl-zoom-out"
            >
              <ZoomOut size={13} />
            </button>
            <button
              type="button"
              className="tl-zoom-level"
              onClick={resetZoom}
              title="Restablecer zoom"
              data-testid="tl-zoom-reset"
            >
              {Math.round(zoom * 100)}%
            </button>
            <button
              type="button"
              className="tl-zoom-btn"
              onClick={zoomIn}
              title="Acercar (más px por segundo)"
              data-testid="tl-zoom-in"
            >
              <ZoomIn size={13} />
            </button>
          </div>

          {!LAYOUT_V2 && (
          <div className="tl-res-group" data-testid="tl-res-group">
            {['720p', '1080p', '4k'].map((r) => (
              <button key={r} className={`tl-res-btn ${resolution === r ? 'active' : ''}`} onClick={() => setResolution(r)} data-testid={`tl-res-${r}`}>
                {RES_LABEL[r]}
              </button>
            ))}
          </div>

          )}

          {/* UI-3: en el layout nuevo resolución y costo viven en el menú de Exportar MP4 */}
          {!LAYOUT_V2 && (
          <div className="tl-cost-badge" data-testid="tl-cost-badge">
            <span className="tl-cost-num">{cost}</span>
            <span className="tl-cost-label">créditos</span>
          </div>
          )}

          {/* E2 — estado de guardado en el servidor */}
          {TIMELINE_SERVER_SYNC && currentProjectId && (
            <span className={`tl-sync-badge ${syncStatus}`} data-testid="tl-sync-status">
              {syncStatus === 'saving' ? <Loader2 size={11} className="spin" /> : null}
              {syncStatus === 'saving' ? 'Guardando…'
                : syncStatus === 'saved' ? 'Guardado'
                : syncStatus === 'offline' ? 'Sin conexión · respaldo local'
                : ''}
            </span>
          )}
          {/* E3 — plantillas de timeline */}
          {TIMELINE_TEMPLATES && (
            <div className="tl-tpl-group">
              <button
                className="tl-icon-btn" onClick={() => setTemplatesOpen(true)}
                title="Plantillas" data-testid="tl-templates-btn"
              >
                <LayoutTemplate size={15} />
              </button>
              <button
                className="tl-icon-btn" onClick={() => setSaveTemplateOpen(true)}
                title="Guardar como plantilla"
                disabled={!currentProjectId || totalDuration <= 0}
                data-testid="tl-save-template-btn"
              >
                <BookmarkPlus size={15} />
              </button>
              {slotStatus.empty > 0 && (
                <button
                  className="tl-slot-badge" onClick={() => setCompleteOpen(true)}
                  title="Completar los huecos de la plantilla"
                  data-testid="tl-complete-template-btn"
                >
                  <AlertTriangle size={11} /> {slotStatus.empty} hueco(s)
                </button>
              )}
            </div>
          )}

          {/* UI-3: en el layout nuevo "Exportar MP4" está en la barra principal
              y el tamaño del preview junto a la barra de reproducción. */}
          {!LAYOUT_V2 && (
            <>
              <button className="tl-export-btn" onClick={handleExport} disabled={rendering || totalDuration <= 0} data-testid="tl-export-btn">
                {rendering ? <Loader2 size={14} className="spin" /> : <Download size={14} />}
                {rendering ? 'Renderizando...' : 'Exportar MP4'}
              </button>
              <button className="tl-icon-btn" onClick={closeEditor} title="Cerrar" data-testid="tl-close-btn"><X size={16} /></button>
            </>
          )}
        </div>
      </div>
      )}

      {renderJob && (
        <div className={`tl-render-status tl-render-${renderJob.status}`} data-testid="tl-render-status">
          {(renderJob.status === 'queuing' || renderJob.status === 'rendering') && (
            <>
              <Loader2 size={14} className="spin" />
              <span data-testid="tl-render-status-text">
                {renderJob.status === 'queuing'
                  ? 'Encolando job…'
                  : (RENDER_STAGE_LABELS[renderJob.stage] || 'Renderizando video…')}
              </span>
              <RenderEta startedAt={renderJob.startedAt} resolution={resolution} duration={totalDuration} />
              {typeof renderJob.progress === 'number' && (
                <div className="tl-render-progress" data-testid="tl-render-progress">
                  <div
                    className="tl-render-progress-fill"
                    style={{ width: `${Math.min(100, Math.max(0, renderJob.progress))}%` }}
                  />
                  <span className="tl-render-progress-pct" data-testid="tl-render-progress-pct">
                    {renderJob.progress}%
                  </span>
                </div>
              )}
              <span className="tl-render-cost">· {renderJob.cost_credits || cost} créditos</span>
            </>
          )}

          {renderJob.status === 'completed' && (
            <>
              <CheckCircle size={14} />
              <span data-testid="tl-render-status-text">Completado · listo para descargar</span>
              <button
                type="button"
                onClick={handleDownload}
                className="tl-render-cta"
                data-testid="tl-render-download"
              >
                <Download size={12} /> Descargar MP4
              </button>
              <button
                type="button"
                onClick={() => useTimelineStore.getState().clearRenderJob()}
                className="tl-render-close"
                title="Cerrar"
              >
                <X size={12} />
              </button>
            </>
          )}

          {renderJob.status === 'downloading' && (
            <>
              <Loader2 size={14} className="spin" />
              <span data-testid="tl-render-status-text">Descargando…</span>
            </>
          )}

          {renderJob.status === 'saving' && (
            <>
              <Loader2 size={14} className="spin" />
              <span data-testid="tl-render-status-text">Guardando en Galería…</span>
            </>
          )}

          {renderJob.status === 'failed' && (
            <>
              <AlertCircle size={14} />
              <span className="tl-render-error" data-testid="tl-render-status-text">{renderJob.error}</span>
              <button
                type="button"
                onClick={handleExport}
                className="tl-render-cta"
                data-testid="tl-render-retry"
              >
                Reintentar
              </button>
              <button
                type="button"
                onClick={() => useTimelineStore.getState().clearRenderJob()}
                className="tl-render-close"
                title="Cerrar"
              >
                <X size={12} />
              </button>
            </>
          )}
        </div>
      )}

      {LAYOUT_V2 && (
        <CanvasResultsBin
          forceClosed={previewSize === 'l'}
          onNeedRoom={() => pickPreviewSize('m')}
          onRestoreLarge={() => pickPreviewSize('l')}
        />
      )}

      <div className="tl-editor-body" data-x={xMode}>
        <div className="tl-editor-left">
          <MasterPreview playheadTime={playheadTime} isPlaying={isPlaying} />
          <div className="tl-toolbar">
            <button className="tl-toolbar-btn" onClick={() => handleOpenGallery('video')} data-testid="tl-add-from-gallery">
              <FolderOpen size={14} /> Galería
            </button>
            <button
              className="tl-toolbar-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              title={limitMB(uploadLimits, 'video')
                ? `Subir desde la computadora · plan ${uploadLimits.plan_label}: hasta ${limitMB(uploadLimits, 'video')} MB por video, ${limitMB(uploadLimits, 'image')} MB por imagen`
                : 'Subir desde la computadora'}
              data-testid="tl-upload-local"
            >
              {uploading ? <Loader2 size={14} className="spin" /> : <Upload size={14} />}
              {uploading ? 'Subiendo...' : 'Subir'}
            </button>
            <input ref={fileInputRef} type="file" accept="image/*,video/*,audio/*" style={{ display: 'none' }} onChange={handleLocalUpload} data-testid="tl-upload-input" />
            {LAYOUT_V2 && (
              <>
                <button className="tl-toolbar-btn" onClick={() => setTextMenuOpen(true)} data-testid="tl-rail-text">
                  <Type size={14} /> Texto
                </button>
                <button className="tl-toolbar-btn" onClick={() => setStickerOpen(true)} data-testid="tl-rail-sticker">
                  <StickerIcon size={14} /> Sticker
                </button>
                <button
                  className={`tl-toolbar-btn ${sidePanel === 'marca' ? 'active' : ''}`}
                  aria-pressed={sidePanel === 'marca'}
                  onClick={() => setSidePanel(sidePanel === 'marca' ? null : 'marca')}
                  data-testid="tl-rail-brand"
                >
                  <Star size={14} /> Marca
                </button>
                <button
                  className={`tl-toolbar-btn ${sidePanel === 'personajes' ? 'active' : ''}`}
                  aria-pressed={sidePanel === 'personajes'}
                  onClick={() => setSidePanel(sidePanel === 'personajes' ? null : 'personajes')}
                  data-testid="tl-rail-characters"
                >
                  <Users size={14} /> Personajes
                </button>
              </>
            )}
            <button className="tl-toolbar-btn danger" onClick={() => { if (window.confirm('¿Vaciar la timeline?')) clearTimeline(); }} data-testid="tl-clear-btn">
              <Trash2 size={14} /> Vaciar
            </button>
          </div>
          {LAYOUT_V2 && (
            <div className="tl-v2-size tl-v2-size-dock" role="group" aria-label="Tamaño del preview" data-testid="tl-preview-size">
              {[['s', 'S', 'Chico'], ['m', 'M', 'Medio'], ['l', 'L', 'Grande']].map(([k, short, label]) => (
                <button
                  key={k}
                  type="button"
                  className={previewSize === k ? 'active' : ''}
                  aria-pressed={previewSize === k}
                  onClick={() => pickPreviewSize(k)}
                  title={`Preview ${label.toLowerCase()}`}
                  aria-label={`Preview ${label.toLowerCase()}`}
                  data-testid={`tl-preview-size-${k}`}
                >
                  {short}
                </button>
              ))}
            </div>
          )}
        </div>

        {LAYOUT_V2 && (
          <TimelineBar
            onReplace={setReplaceTarget}
            onExport={() => exportRef.current?.()}
            rendering={rendering}
            totalDuration={totalDuration}
            cost={cost}
            costFor={(res) => calcCost(totalDuration, res)}
            viewportWidth={Math.max(0, viewportWidth - LABEL_GUTTER)}
            syncState={syncStatus}
            syncLabel={TIMELINE_SERVER_SYNC && currentProjectId
              ? (syncStatus === 'saving' ? 'Guardando…'
                : syncStatus === 'saved' ? 'Guardado'
                : syncStatus === 'offline' ? 'Sin conexión · respaldo local' : '')
              : ''}
            templates={TIMELINE_TEMPLATES}
            onOpenTemplates={() => setTemplatesOpen(true)}
            onSaveTemplate={() => setSaveTemplateOpen(true)}
            canSaveTemplate={!!currentProjectId && totalDuration > 0}
            emptySlots={slotStatus.empty}
            onOpenComplete={() => setCompleteOpen(true)}
          />
        )}

        {LAYOUT_V2 && sideContent}

        <div className="tl-editor-right">
          <div
            className="tl-tracks-scroll"
            ref={scrollRef}
            data-testid="tl-tracks-scroll"
            onWheel={(e) => {
              // La rueda recorre el timeline a lo largo (y con Ctrl/⌘ hace zoom),
              // así no hace falta pelearse con la barra de desplazamiento.
              if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                if (e.deltaY < 0) zoomIn(); else zoomOut();
                return;
              }
              if (Math.abs(e.deltaY) > Math.abs(e.deltaX) && e.currentTarget.scrollWidth
                  > e.currentTarget.clientWidth) {
                e.preventDefault();
                e.currentTarget.scrollLeft += e.deltaY;
              }
            }}
          >
            {/* The header row has a fixed label column, then the ruler shares
                the same scroll context as the lanes — so t=0 is aligned. */}
            <div className="tl-ruler-row" style={{ width: totalWidth + LABEL_GUTTER }}>
              <div className="tl-ruler-label-gutter" />
              <div className="tl-ruler" style={{ width: totalWidth }} data-testid="tl-ruler">
                {rulerMarks.map((s) => (
                  <div key={s} className="tl-ruler-tick" style={{ left: s * pxPerSec }}>
                    <span>{s}s</span>
                  </div>
                ))}
              </div>
            </div>

            <div
              className="tl-tracks-click-area"
              style={{ width: totalWidth + LABEL_GUTTER }}
              onClick={(e) => {
                // Click to seek — only on the lane area, compensating for label gutter.
                const laneEl = e.currentTarget.querySelector('.tl-track-lane');
                if (!laneEl) return;
                const rect = laneEl.getBoundingClientRect();
                const x = e.clientX - rect.left;
                if (x < 0) return;
                setPlayhead(x / pxPerSec);
              }}
            >
              {/* Lanes ordenados por `trackOrder`: arriba = se dibuja encima.
                  Es el MISMO array que usa el z-index del preview y el render. */}
              {trackOrder.map((track) => (track === 'text' || track === 'sticker' ? (
                <ElementTrack
                  key={track}
                  type={track}
                  totalWidth={totalWidth}
                  onAdd={() => (track === 'text' ? setTextMenuOpen(true) : setStickerOpen(true))}
                  onReorder={startTrackReorder}
                />
              ) : (
                <Track
                  key={track}
                  kind={track}
                  clips={clipsByTrack[track] || []}
                  totalWidth={totalWidth}
                  onAdd={handleOpenGallery}
                  onReorder={startTrackReorder}
                />
              )))}
              <div className="tl-playhead" style={{ left: playheadTime * pxPerSec }} data-testid="tl-playhead">
                <div className="tl-playhead-handle" />
              </div>
              {snapGuide != null && (
                <div className="tl-snap-guide" style={{ left: snapGuide * pxPerSec }} aria-hidden="true" />
              )}
            </div>

            {/* Legacy bottom-of-tracks "add" hints removed — each track now
                exposes its own + button in the label gutter for a cleaner UX. */}
          </div>
        </div>
      </div>

      {!LAYOUT_V2 && sideContent}

      {/* Ventanas del timeline en document.body (TimelinePortal): así quedan
          delante del dock de LMX y de cualquier capa fuera del timeline. */}
      <TimelinePortal>
        {textMenuOpen && (
          <TextPresetMenu
            onClose={() => setTextMenuOpen(false)}
            onPick={(preset) => {
              setTextMenuOpen(false);
              addTextElement({ preset: preset === 'custom' ? 'none' : preset });
            }}
          />
        )}

        {stickerOpen && (
          <StickerPicker
            onClose={() => setStickerOpen(false)}
            onPick={(item) => {
              setStickerOpen(false);
              addStickerElement({
                source_url: item.url,
                asset_id: item.assetId || null,
                label: item.name,
              });
            }}
          />
        )}

        {gallerySel?.open && LAYOUT_V2 && (
          <GalleryPanel
            mode="timeline"
            targetTrack={gallerySel.targetTrack}
            onClose={() => setGallerySel(null)}
          />
        )}
        {replaceTarget && (
          <ReplaceClipModal target={replaceTarget} onClose={() => setReplaceTarget(null)} />
        )}

        {gallerySel?.open && !LAYOUT_V2 && (
          <GalleryPicker
            targetTrack={gallerySel.targetTrack}
            onClose={() => setGallerySel(null)}
            onPick={handleGalleryPick}
          />
        )}

        {/* E3 · plantillas de timeline */}
        {saveTemplateOpen && (
          <SaveAsTemplateModal
            projectId={currentProjectId}
            serverVersion={useTimelineStore.getState().serverVersion}
            onClose={() => setSaveTemplateOpen(false)}
          />
        )}
        {templatesOpen && (
          <TemplatesPanel
            projectId={currentProjectId}
            onClose={() => setTemplatesOpen(false)}
            onApplied={(out) => {
              hydrateTimeline(out.data);
              const store = useTimelineStore.getState();
              store.setServerVersion(out.version);
              store.setSyncStatus('saved');
            }}
            onOpenComplete={() => setCompleteOpen(true)}
          />
        )}
        {completeOpen && (
          <CompleteTemplatePanel
            projectId={currentProjectId}
            onClose={() => setCompleteOpen(false)}
            onChanged={reloadFromServer}
          />
        )}

        {/* E6 · conflicto del timeline: Recargar del servidor / Mantener lo mío */}
        {syncConflict && <TimelineConflictModal projectId={currentProjectId} />}
      </TimelinePortal>
    </div>
  );
};

export default TimelineEditor;
